import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormControl, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { InstructionBuilderService } from '../../../automation/instructions/instruction-builder/services/instruction-builder.service';
import { ApiService } from '../../../../services/api.service';
import { ISensitivityLabelExpanded } from '../../../../interfaces/i-sensitivity-label';
import { PromptLibraryService } from '../../services/prompt-library.service';
import { IInstructionsDetails } from '../../../automation/interfaces/i-instructions';
import { CheckableSettings, TreeItemLookup, TreeViewModule } from '@progress/kendo-angular-treeview';
import { CUI_PARENT_CODE } from '../../../../constants/sensitivity.constants';
import { SensitivityLabelService } from '../../../../services/sensitivity-label.service';
import { mapLegacySensitivityLabel, toISensitivityLabel } from '../../../../utilities/sensitivity-label.utils';
import { ChatSessionService } from '../../services/chat-session.service';
import { AlertService } from '../../../../shared/alert/alert.service';
import { AlertEnum } from '../../../../shared/alert/alert.enum';
import { IUser } from '../../../../interfaces/i-user-profile';
import { ProfileService } from '../../../../services/profile.service';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTooltipModule } from '@angular/material/tooltip';
import { InstructionType } from '../../../../enums/automation.enum';

@Component({
  selector: 'app-quick-add-prompt-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatExpansionModule,
    MatTooltipModule,
    TreeViewModule
  ],
  templateUrl: './quick-add-prompt-dialog.component.html',
  styleUrl: './quick-add-prompt-dialog.component.scss'
})
export class QuickAddPromptDialogComponent implements OnInit {
  name = new FormControl('', [Validators.required, Validators.maxLength(100)]);
  prompt = new FormControl('', [Validators.required]);
  type = new FormControl({ value: 'AI Prompt', disabled: true });
  sensitivityLabels = new FormControl<string[]>([], [this.sensitivityLabelValidator.bind(this)]);
  promptForm: FormGroup;

  tooltipText = 'Add an easy to identify or meaningful name for your prompt. This will help you quickly find it in the Automation Library to use later.';

  readonly dialogRef = inject(MatDialogRef<QuickAddPromptDialogComponent>);
  readonly data = inject<string>(MAT_DIALOG_DATA);
  readonly #instructionBuilderService = inject(InstructionBuilderService);
  readonly #apiService = inject(ApiService);
  readonly #alertService = inject(AlertService);
  readonly #promptLibraryService = inject(PromptLibraryService);
  readonly #chatSessionService = inject(ChatSessionService);
  readonly #sensitivityLabelService = inject(SensitivityLabelService);
  /** Whether the sensitivity-label feature is enabled; label UI is hidden when disabled. */
  readonly slEnabled = this.#sensitivityLabelService.enabled;

  // TreeView properties
  expandedKeys: string[] = [CUI_PARENT_CODE];
  checkedKeys: string[] = [];
  readonly sensitivityLabelData = this.#sensitivityLabelService.labels;
  checkableSettings: CheckableSettings = {
    checkChildren: true,
    checkDisabledChildren: true, // Check all children
    checkParents: true,
    enabled: true,  // Enable checkable mode
    mode: 'multiple', // Multiple selection mode
    checkOnClick: true, // Check all parents when children are checked
    uncheckCollapsedChildren: false // Uncheck all children when parent is unchecked
  };

  #profileService = inject(ProfileService);

  constructor() {
    // Get the current session
    const currentSession = this.#chatSessionService.selectedSession();

    // Set default sensitivity labels from the current session if available
    if (currentSession?.sensitivityLabels && currentSession.sensitivityLabels.length > 0) {
      // Map backend ISensitivityLabel to the new GUID-based id for the TreeView
      const initialKeys = currentSession.sensitivityLabels
        .map(label => mapLegacySensitivityLabel(label)?.id)
        .filter((id): id is string => !!id);

      // Ensure parents are selected for all session labels
      this.checkedKeys = this.ensureParentsSelected(initialKeys);

      // Update the form value
      this.sensitivityLabels.setValue([...this.checkedKeys]);
    }

    // Pre-populate form fields
    if (this.data) {
      this.name.setValue(this.data.substring(0, 100));
      this.prompt.setValue(this.data);
    }

    this.promptForm = new FormGroup({
      name: new FormControl(this.name.value, [Validators.required, Validators.maxLength(100)]),
      prompt: new FormControl(this.prompt.value, [Validators.required]),
      type: new FormControl({ value: 'AI Prompt', disabled: true }),
      sensitivityLabels: new FormControl<string[]>(this.checkedKeys, [this.sensitivityLabelValidator.bind(this)])
    });
  }

  /**
   * Validator for sensitivity label selection
   */
  sensitivityLabelValidator(control: AbstractControl): ValidationErrors | null {
    const selectedLabels = control.value as any[];
    if (selectedLabels.length === 0 && control.touched) {
      return { sensitivityLabelInvalid: true };
    }
    return null;
  }

  /**
   * Decides whether the checkbox should be displayed for the item.
   * CUI parent and its children are hidden from direct selection.
   */
  hasCheckbox = (item: object, _index: string): boolean => {
    const label = item as ISensitivityLabelExpanded;
    return !(label.parent === CUI_PARENT_CODE || label.id === CUI_PARENT_CODE);
  };

  ngOnInit(): void {

  }

  /**
   * Handle TreeView selection change
   */
  onSelectionChange(event: TreeItemLookup): void {
    // First get the current selection
    const currentSelection = [...this.checkedKeys];

    // Check if we need to select parents
    const selectionWithParents = this.ensureParentsSelected(currentSelection);

    // Update checkedKeys if parent selection was added
    if (selectionWithParents.length > currentSelection.length) {
      this.checkedKeys = selectionWithParents;
    }

    // Update form control
    this.sensitivityLabels.setValue([...this.checkedKeys]);
    this.sensitivityLabels.markAsTouched();
    this.sensitivityLabels.updateValueAndValidity();
  }

  /**
   * Ensures that when a child is selected, all parent nodes are also selected.
   * @param selectedIds The currently selected node GUID strings
   * @returns Array of GUID strings including parents
   */
  private ensureParentsSelected(selectedIds: string[]): string[] {
    const result = new Set<string>(selectedIds);
    const labels = this.sensitivityLabelData();

    for (const id of selectedIds) {
      let currentNode = labels.find(node => node.id === id);
      while (currentNode && currentNode.parent) {
        result.add(currentNode.parent);
        currentNode = labels.find(node => node.id === currentNode!.parent);
      }
    }

    return Array.from(result);
  }

  quickAddPrompt(): void {
    if (this.promptForm.valid) {
      // Get default instruction
      const instruction = this.#instructionBuilderService.createInstructionDefaultValue();

      const owner: IUser = {
        name: this.#profileService.userProfile()!.profile.name,
        userId: this.#profileService.userProfile()!.profile.email,
      };

      // Create instruction details
      const instructionDetails: IInstructionsDetails = {
        ...instruction.details,
        name: this.promptForm.get('name')?.value as string,
        prompt: this.promptForm.get('prompt')?.value as string,
        type: InstructionType.AiPrompt,
        owners: [owner],
      };

      // Get selected labels — convert ISensitivityLabelExpanded → ISensitivityLabel for backend
      const selectedLabelIds = this.sensitivityLabels.value;
      if (selectedLabelIds && selectedLabelIds.length > 0) {
        instructionDetails.sensitivityLabels = this.#sensitivityLabelService.labels()
          .filter(label => selectedLabelIds.includes(label.id))
          .map(label => toISensitivityLabel(label));
      }

      // Save instruction
      this.#apiService.saveInstruction(instructionDetails).subscribe({
        next: () => {
          this.#alertService.showAlert({
            message: 'Prompt has been saved successfully',
            type: AlertEnum.success,
            duration: 3
          });

          // Reload instructions
          this.#promptLibraryService.loadInstructions();

          // Close dialog
          this.dialogRef.close(true);
        },
        error: (error) => {
          this.#alertService.showAlert({
            message: 'Error saving prompt: ' + (error.message || 'Unknown error'),
            type: AlertEnum.error,
            duration: 5
          });
        }
      });
    }
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }
}
